import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import CreateFeeScheduleCategory from "_shared/fee-schedules/categories/_views/create-fee-schedule-category";
import FeeScheduleBillingCode from "_shared/fee-schedules/categories/billing-codes/fee-schedule-billing-code";
import FeeScheduleCategoryDescription from "_shared/fee-schedules/categories/descriptions/fee-schedule-category-description";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  type MRT_Row,
  MRT_TableInstance,
  MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FeeScheduleCategoryMaskedCurrency } from "work/_shared/masked-fields";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import FeeRedlineInput from "work/proposals/redlining/fee-schedule-redline/fee-redline/_views/fee-redline-input";
import { FeeScheduleCategoryRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-category-redline";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import TextRedlineInput from "work/proposals/redlining/field-redlines/_views/text-redline-input";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";
import { MRTNoMemo } from "_shared/_views/tables/table";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
  marginBottom: theme.spacing(2),
}));
const RedlineText = styled(TextRedlineInput)(() => ({
  margin: 0,
  minWidth: "unset",
  width: "100%",
}));
const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));

type FeeScheduleRedlineSelectionProps = {
  className?: string;
  feeScheduleRedline: FeeScheduleRedline;
  disableEditing?: boolean;
  commentThreads: CommentThread[];
  activeReviewField: ProposalField | undefined;
  onCommentsClicked: (field: ProposalField, name?: string) => void;
  onCategoryClicked?: (categoryId: Guid) => void;
  onFeeScheduleRedlineChange: (newRedline: FeeScheduleRedline, traversalFieldOverride?: ProposalField | null) => void;
};

const FeeScheduleRedlineSelection = (props: Readonly<FeeScheduleRedlineSelectionProps>) => {
  const {
    className,
    feeScheduleRedline,
    commentThreads,
    disableEditing,
    activeReviewField,
    onCommentsClicked,
    onCategoryClicked,
    onFeeScheduleRedlineChange,
  } = props;

  const prevVisibilityRef = useRef<Record<string, boolean> | null>(null);
  const prevSizingRef = useRef<Record<string, number> | null>(null);

  const confirm = useConfirmDialog();
  const { openDialog, popDialog } = useDialog();

  useEffect(() => {
    const target = document.getElementById(activeReviewField?.id?.value ?? "");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeReviewField]);

  const handleRemoveClicked = useCallback(
    (categoryId: Guid) => {
      const newRedline = feeScheduleRedline.removeEntryByFieldId(categoryId);
      onFeeScheduleRedlineChange(newRedline, newRedline.field);
      onCommentsClicked?.(ProposalField.FeeSchedule, undefined);
    },
    [feeScheduleRedline, onFeeScheduleRedlineChange, onCommentsClicked],
  );

  const enterFocusMode = useCallback((table: MRT_TableInstance<FeeScheduleCategoryRedline>, columnId: string) => {
    const tableState = table.getState();

    // Snapshot existing state
    prevVisibilityRef.current ??= { ...tableState.columnVisibility };
    prevSizingRef.current ??= { ...tableState.columnSizing };

    const keepVisible = new Set<string>([columnId, "mrt-row-actions", "name"]);

    // Build column visibility map
    const nextVisibility: Record<string, boolean> = {};
    table.getAllLeafColumns().forEach((col) => {
      nextVisibility[col.id] = keepVisible.has(col.id);
    });

    table.setColumnVisibility(nextVisibility);
  }, []);

  const exitFocusMode = useCallback((table: MRT_TableInstance<FeeScheduleCategoryRedline>) => {
    if (prevVisibilityRef.current) {
      table.setColumnVisibility(prevVisibilityRef.current);
      prevVisibilityRef.current = null;
    }
    if (prevSizingRef.current) {
      table.setColumnSizing(prevSizingRef.current);
      prevSizingRef.current = null;
    }
  }, []);

  const renderBillingCodeRedline = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>, rowData: FeeScheduleCategoryRedline) => {
      if (!rowData.field.id) {
        console.error("Category Redline ID is malformed", rowData);
        return;
      }
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      if (!categoryRedline.billingCodeRedline) {
        console.error("Category Redline billing code is malformed", categoryRedline);
        return;
      }
      return (
        <RedlineText
          variant="standard"
          size="small"
          margin="none"
          fullWidth
          hideLabel
          showPlaceholder
          textRedline={categoryRedline.billingCodeRedline}
          readOnly={disableEditing || categoryRedline.isRemoved}
          hideUndoButton={true}
          hideAcceptRejectButtons={true}
          hideComments={true}
          fieldConstructor={FeeScheduleBillingCode}
          onShowTextInput={() => enterFocusMode(table, "billingCode")}
          onHideTextInput={() => exitFocusMode(table)}
          onTextFieldRedlineChange={async (
            newTextRedline: FieldRedline<FeeScheduleBillingCode>,
            traversalFieldOverride?: ProposalField | null,
          ) => {
            const existingCategory = categoryRedline.currentEntry ?? categoryRedline.revisedEntry;
            if (!existingCategory || !newTextRedline.currentEntry) {
              console.error("category redline is malformed", categoryRedline);
              return;
            }
            const updatedCategory =
              existingCategory instanceof FeeScheduleCategory
                ? FeeScheduleCategory.create({
                    name: existingCategory.name,
                    description: existingCategory.description,
                    fee: existingCategory.fee,
                    billingCode: newTextRedline.currentEntry,
                    id: existingCategory.id,
                  })
                : new FeeScheduleCategoryMaskedCurrency({
                    id: existingCategory.id,
                    name: existingCategory.name,
                    description: existingCategory.description,
                    billingCode: newTextRedline.currentEntry,
                  });
            const newRedline = feeScheduleRedline.replaceEntryById(updatedCategory.id, updatedCategory);
            onFeeScheduleRedlineChange(newRedline, traversalFieldOverride);
            onCommentsClicked?.(ProposalField.FeeSchedule, undefined);
          }}
        />
      );
    },
    [disableEditing, enterFocusMode, exitFocusMode, feeScheduleRedline, onFeeScheduleRedlineChange, onCommentsClicked],
  );

  const renderDescriptionRedline = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>, rowData: FeeScheduleCategoryRedline) => {
      if (!rowData.field.id) {
        console.error("Category Redline ID is malformed", rowData);
        return;
      }
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      if (!categoryRedline.descriptionRedline) {
        console.error("Category Redline description is malformed", categoryRedline);
        return;
      }
      return (
        <RedlineText
          variant="standard"
          size="small"
          margin="none"
          fullWidth
          hideLabel
          showPlaceholder
          hideUndoButton={true}
          hideAcceptRejectButtons={true}
          textRedline={categoryRedline.descriptionRedline}
          fieldConstructor={FeeScheduleCategoryDescription}
          onShowTextInput={() => enterFocusMode(table, "description")}
          onHideTextInput={() => exitFocusMode(table)}
          onTextFieldRedlineChange={async (
            newTextRedline: FieldRedline<FeeScheduleCategoryDescription>,
            traversalFieldOverride?: ProposalField | null,
          ) => {
            const existingCategory = categoryRedline.currentEntry ?? categoryRedline.revisedEntry;
            if (!existingCategory || !newTextRedline.currentEntry) {
              console.error("category redline is malformed", categoryRedline);
              return;
            }
            const updatedCategory =
              existingCategory instanceof FeeScheduleCategory
                ? FeeScheduleCategory.create({
                    name: existingCategory.name,
                    description: newTextRedline.currentEntry,
                    fee: existingCategory.fee,
                    billingCode: existingCategory.billingCode,
                    id: existingCategory.id,
                  })
                : new FeeScheduleCategoryMaskedCurrency({
                    id: existingCategory.id,
                    name: existingCategory.name,
                    description: newTextRedline.currentEntry,
                    billingCode: existingCategory.billingCode,
                  });

            const newRedline = feeScheduleRedline.replaceEntryById(updatedCategory.id, updatedCategory);
            onFeeScheduleRedlineChange(newRedline, traversalFieldOverride);
            onCommentsClicked?.(ProposalField.FeeSchedule, undefined);
          }}
          readOnly={disableEditing || categoryRedline.isRemoved}
          hideComments={true}
        />
      );
    },
    [disableEditing, enterFocusMode, exitFocusMode, feeScheduleRedline, onFeeScheduleRedlineChange, onCommentsClicked],
  );

  const renderFeeRedline = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>, rowData: FeeScheduleCategoryRedline) => {
      if (
        rowData.originalEntry instanceof FeeScheduleCategoryMaskedCurrency ||
        rowData.revisedEntry instanceof FeeScheduleCategoryMaskedCurrency
      ) {
        return <em>Masked</em>;
      }
      if (!rowData.field.id) {
        console.error("Category Redline ID is malformed", rowData);
        return;
      }
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      if (!categoryRedline.feeRedline) {
        console.error("Category Redline fee is malformed", categoryRedline);
        return;
      }

      return (
        <FeeRedlineInput
          variant="standard"
          size="small"
          margin="none"
          categoryRedline={categoryRedline}
          readOnly={disableEditing || categoryRedline.isRemoved}
          onShowTextInput={() => enterFocusMode(table, "fee")}
          onHideTextInput={() => exitFocusMode(table)}
          onFeeChange={(newFee: Fee) => {
            const existingCategory = categoryRedline.currentEntry ?? categoryRedline.revisedEntry;
            if (!existingCategory) {
              console.error("category redline is malformed", categoryRedline);
              return;
            }
            const updatedCategory =
              existingCategory instanceof FeeScheduleCategory
                ? FeeScheduleCategory.create({
                    name: existingCategory.name,
                    description: existingCategory.description,
                    fee: newFee,
                    billingCode: existingCategory.billingCode,
                    id: existingCategory.id,
                  })
                : new FeeScheduleCategoryMaskedCurrency({
                    id: existingCategory.id,
                    name: existingCategory.name,
                    description: existingCategory.description,
                    billingCode: existingCategory.billingCode,
                  });

            const newRedline = feeScheduleRedline.replaceEntryById(updatedCategory.id, updatedCategory);
            onFeeScheduleRedlineChange(newRedline);
            onCommentsClicked?.(ProposalField.FeeSchedule, undefined);
          }}
        />
      );
    },
    [disableEditing, enterFocusMode, exitFocusMode, feeScheduleRedline, onFeeScheduleRedlineChange, onCommentsClicked],
  );

  const renderNameRedline = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>, rowData: FeeScheduleCategoryRedline) => {
      if (!rowData.field.id) {
        console.error("Category Redline ID is malformed", rowData);
        return;
      }
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      if (!categoryRedline.nameRedline) {
        console.error("Category Redline Name is malformed", categoryRedline);
        return;
      }
      return categoryRedline.isAccepted && categoryRedline.isRemoved ? (
        "Removed"
      ) : (
        <RedlineText
          id={categoryRedline.field.id?.value}
          bold={true}
          variant="standard"
          size="small"
          margin="none"
          hideLabel
          showPlaceholder
          hideUndoButton={true}
          hideAcceptRejectButtons={true}
          fullWidth
          textRedline={categoryRedline.nameRedline}
          fieldConstructor={FeeScheduleCategoryName}
          onShowTextInput={() => enterFocusMode(table, "name")}
          onHideTextInput={() => exitFocusMode(table)}
          onTextFieldRedlineChange={(
            newTextRedline: FieldRedline<FeeScheduleCategoryName>,
            traversalFieldOverride?: ProposalField | null,
          ) => {
            const duplicateName = feeScheduleRedline.redlines.find((redline) => {
              if (!redline.nameRedline) {
                console.error("Category Redline Name is malformed", redline);
                return false;
              }
              return redline.nameRedline.currentEntry === newTextRedline.currentEntry;
            });
            if (duplicateName) {
              enqueueSnackbar("A category with the same name already exists", {
                variant: "warning",
              });
              return;
            }

            const existingCategory = categoryRedline.currentEntry ?? categoryRedline.revisedEntry;
            if (!existingCategory || !newTextRedline.currentEntry) {
              console.error("category redline is malformed", categoryRedline);
              return;
            }
            const updatedCategory =
              existingCategory instanceof FeeScheduleCategory
                ? FeeScheduleCategory.create({
                    id: existingCategory.id,
                    name: newTextRedline.currentEntry,
                    description: existingCategory.description,
                    fee: existingCategory.fee,
                    billingCode: existingCategory.billingCode,
                  })
                : new FeeScheduleCategoryMaskedCurrency({
                    id: existingCategory.id,
                    name: newTextRedline.currentEntry,
                    description: existingCategory.description,
                    billingCode: existingCategory.billingCode,
                  });

            const newRedline = feeScheduleRedline.replaceEntryById(updatedCategory.id, updatedCategory);
            onFeeScheduleRedlineChange(newRedline, traversalFieldOverride);
            onCommentsClicked?.(ProposalField.FeeSchedule, undefined);
          }}
          readOnly={disableEditing || categoryRedline.isRemoved}
          hideComments={true}
        />
      );
    },
    [disableEditing, enterFocusMode, exitFocusMode, feeScheduleRedline, onFeeScheduleRedlineChange, onCommentsClicked],
  );

  const columnHelper = useMemo(() => createMRTColumnHelper<FeeScheduleCategoryRedline>(), []);
  const columns: Array<ReturnType<typeof columnHelper.display>> = useMemo(
    () => [
      columnHelper.display({
        header: "Name",
        id: "name",
        Cell: ({ table, row }) => renderNameRedline(table, row.original),
      }),
      columnHelper.display({
        header: "Description",
        id: "description",
        Cell: ({ table, row }) => renderDescriptionRedline(table, row.original),
      }),
      columnHelper.display({
        header: "Fee",
        id: "fee",
        Cell: ({ table, row }) => renderFeeRedline(table, row.original),
      }),
      columnHelper.display({
        header: "Billing Code",
        id: "billingCode",
        Cell: ({ table, row }) => renderBillingCodeRedline(table, row.original),
      }),
    ],
    [columnHelper, renderBillingCodeRedline, renderDescriptionRedline, renderFeeRedline, renderNameRedline],
  );

  const renderRowActions = useCallback(
    (props: { row: MRT_Row<FeeScheduleCategoryRedline> }) => {
      const { row } = props;
      if (!row.original.field.id) return null;

      const category = Object.assign(new FeeScheduleCategoryRedline(row.original.field.id, null, null), row.original);

      if (!category) return null;
      if (disableEditing) {
        return (
          <Box sx={{ textAlign: "center" }}>
            <CommentsButton
              commentThreads={commentThreads}
              field={category.field}
              toolTip="Comments"
              onCommentsClicked={() => {
                onCommentsClicked?.(
                  category.field,
                  category?.currentEntry?.name?.value ?? category?.revisedEntry?.name?.value,
                );
              }}
            />
          </Box>
        );
      }

      return (
        <Box sx={{ textAlign: "center" }}>
          {category.isResolved && !category.isRemoved && !category.isNewlyAdded && (
            <Tooltip title="Remove Fee">
              <span>
                <ActionButton onClick={() => category.field.id && handleRemoveClicked(category.field.id)}>
                  <DeleteOutlineIcon color="error" />
                </ActionButton>
              </span>
            </Tooltip>
          )}
          {!category.isResolved && (
            <>
              <Tooltip title="Accept Changes">
                <span>
                  <ActionButton
                    onClick={() => {
                      onFeeScheduleRedlineChange(feeScheduleRedline.updateRedline(category.accept()));
                      onCommentsClicked(ProposalField.FeeSchedule, undefined);
                    }}
                  >
                    <CheckIcon color="success" />
                  </ActionButton>
                </span>
              </Tooltip>
              <Tooltip title="Reject Changes">
                <span>
                  <ActionButton
                    onClick={() => {
                      onFeeScheduleRedlineChange(feeScheduleRedline.updateRedline(category.reject()));
                      onCommentsClicked(ProposalField.FeeSchedule, undefined);
                    }}
                  >
                    <CloseIcon color="error" />
                  </ActionButton>
                </span>
              </Tooltip>
            </>
          )}
          {category.canBeUndone && (
            <Tooltip title="Undo Changes">
              <span>
                <ActionButton
                  onClick={() => {
                    if (category.field.id) {
                      onFeeScheduleRedlineChange(feeScheduleRedline.undoRedlineById(category.field.id), category.field);
                      onCommentsClicked?.(ProposalField.FeeSchedule, undefined);
                    }
                  }}
                >
                  <UndoIcon color="secondary" />
                </ActionButton>
              </span>
            </Tooltip>
          )}
          <CommentsButton
            commentThreads={commentThreads}
            field={category.field}
            isActive={activeReviewField?.isEqualTo(category.field)}
            toolTip="Comments"
            onCommentsClicked={() => {
              onCommentsClicked?.(
                category.field,
                category?.currentEntry?.name?.value ?? category?.revisedEntry?.name?.value,
              );
            }}
          />
        </Box>
      );
    },
    [
      disableEditing,
      commentThreads,
      activeReviewField,
      onCommentsClicked,
      handleRemoveClicked,
      onFeeScheduleRedlineChange,
      feeScheduleRedline,
    ],
  );

  /**
   * Handles when the add button is clicked
   */
  const beginAddCategory = useCallback((): void => {
    openDialog({
      title: "Create Fee Schedule Category",
      component: (
        <CreateFeeScheduleCategory
          onCategoryAdded={async (newCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => {
            let newRedline: FeeScheduleRedline;
            if (!newCategory) return;

            const matchingRedline = feeScheduleRedline.getMatchingCategoryRedline(newCategory);
            if (matchingRedline) {
              const response = await confirm({
                title: "Overwrite Category?",
                message: "A category with the same name already exists. Do you want to overwrite it?",
                okButtonText: "Overwrite",
              });

              if (response === ConfirmResponse.Cancel) {
                return;
              }
              newRedline = feeScheduleRedline.replaceEntryById(newCategory.id, newCategory);
            } else {
              newRedline = feeScheduleRedline.addEntry(newCategory);
            }

            onFeeScheduleRedlineChange(newRedline, ProposalField.FeeScheduleCategory(newCategory.id));
            onCommentsClicked?.(ProposalField.FeeSchedule, undefined);
          }}
          popDialog={popDialog}
        />
      ),
    });
  }, [openDialog, popDialog, feeScheduleRedline, onFeeScheduleRedlineChange, onCommentsClicked, confirm]);

  const tableOptions: MRT_TableOptions<FeeScheduleCategoryRedline> = useMemo(
    () => ({
      columns,
      data: feeScheduleRedline.redlines,
      enableKeyboardShortcuts: false,
      muiPaginationProps: {
        color: "primary",
        shape: "rounded",
        showRowsPerPage: false,
        variant: "outlined",
      },
      paginationDisplayMode: "pages",
      enableRowSelection: false,
      enableTableHead: true,
      getRowId: (row) => row.field.id?.value ?? Guid.generate().value,
      initialState: {
        showColumnFilters: false,
        columnPinning: { right: ["mrt-row-actions"] },
        density: "comfortable",
      },
      enableColumnPinning: true,
      manualFiltering: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      enableRowActions: true,
      muiTableProps: ({ table }) => ({
        sx: {
          borderSpacing: table.getRowModel().rows.some((row) => row.id === activeReviewField?.id?.value) ? "3px" : 0,
        },
      }),
      muiTableBodyRowProps: ({ row }) => ({
        hover: false,
        onClick: () => {
          if (!row.original.field.id) return;
          onCategoryClicked?.(row.original.field.id);
        },
        sx: {
          animation: row.original.field?.isEqualTo(activeReviewField) ? `${pulseBorder} 1.5s infinite` : "none",
        },
      }),
      renderEmptyRowsFallback: () => {
        return (
          <Box>
            <Typography sx={{ p: 4, display: "flex", justifyContent: "center" }} variant="body1" color="textSecondary">
              No fee schedule categories
            </Typography>
          </Box>
        );
      },
      renderTopToolbarCustomActions: () => {
        return (
          <ActionsContainer>
            {!disableEditing && (
              <>
                <Button startIcon={<AddIcon />} onClick={beginAddCategory} variant="outlined" size="small">
                  Add New Category
                </Button>
                {!feeScheduleRedline.isResolved && (
                  <>
                    <IconButton
                      onClick={() => {
                        onFeeScheduleRedlineChange(feeScheduleRedline.acceptAll());
                        onCommentsClicked(ProposalField.FeeSchedule, undefined);
                      }}
                    >
                      <CheckIcon color="success" />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        onFeeScheduleRedlineChange(feeScheduleRedline.rejectAll());
                        onCommentsClicked(ProposalField.FeeSchedule, undefined);
                      }}
                    >
                      <CloseIcon color="error" />
                    </IconButton>
                  </>
                )}
                {feeScheduleRedline.canBeUndone && (
                  <IconButton
                    onClick={() => {
                      onFeeScheduleRedlineChange(feeScheduleRedline.undoAll(), null);
                      onCommentsClicked(ProposalField.FeeSchedule, undefined);
                    }}
                  >
                    <UndoIcon color="secondary" />
                  </IconButton>
                )}
              </>
            )}
          </ActionsContainer>
        );
      },
      renderToolbarInternalActions: ({ table }) => (
        <Box>
          <ToggleGlobalFilterButton table={table} />
          <ToggleFiltersButton table={table} />
          <ShowHideColumnsButton table={table} />
          <ToggleDensePaddingButton table={table} />
          <ToggleFullScreenButton table={table} />
          <CommentsButton
            commentThreads={commentThreads}
            field={ProposalField.FeeSchedule}
            isActive={activeReviewField?.isEqualTo(ProposalField.FeeSchedule)}
            toolTip="Comments"
            onCommentsClicked={() => {
              onCommentsClicked?.(ProposalField.FeeSchedule);
            }}
          />
        </Box>
      ),
      renderRowActions: renderRowActions,
      muiTableContainerProps: ({ table }) => {
        const top = table.refs.topToolbarRef.current?.offsetHeight ?? 0;
        const bottom = table.refs.bottomToolbarRef.current?.offsetHeight ?? 0;

        return {
          sx: {
            height: `calc(100% - ${top}px - ${bottom}px)`,
            minHeight: 0,
          },
        };
      },
      muiTablePaperProps: {
        elevation: 0,
        sx: {
          height: "100%",
        },
      },
    }),
    [
      activeReviewField,
      beginAddCategory,
      columns,
      commentThreads,
      disableEditing,
      feeScheduleRedline,
      onCategoryClicked,
      onCommentsClicked,
      onFeeScheduleRedlineChange,
      renderRowActions,
    ],
  );

  return (
    <div className={className}>
      <MRTNoMemo<FeeScheduleCategoryRedline> options={tableOptions} />;
    </div>
  );
};

export default FeeScheduleRedlineSelection;
